/**
 * Customer-portal RBAC (Doc §5.4). Mirrors the backend scope names so the UI can hide actions a
 * principal cannot perform — the backend `requireScope` remains the real enforcement boundary. These
 * are PORTAL roles (who logs into the customer dashboard), distinct from the API-key scopes a tenant
 * mints for machine integrations.
 */

export const CUSTOMER_PORTAL_ROLES = {
  owner: [
    'tenant:read', 'tenant:write', 'tenant:verify_email', 'users:read', 'keys:read', 'keys:write', 'keys:rotate',
    'webhooks:read', 'webhooks:write', 'policies:read', 'policies:write', 'branding:read',
    'branding:write', 'reports:read', 'reports:export', 'safety:read', 'events:read', 'usage:read', 'billing:read',
    'support:read', 'support:write', 'docs:read', 'sdk:download', 'audit:read', 'corridors:read',
  ],
  admin: [
    'tenant:read', 'tenant:write', 'tenant:verify_email', 'users:read', 'keys:read', 'keys:write', 'keys:rotate',
    'webhooks:read', 'webhooks:write', 'policies:read', 'policies:write', 'branding:read',
    'branding:write', 'reports:read', 'reports:export', 'safety:read', 'events:read', 'usage:read', 'billing:read',
    'support:read', 'support:write', 'docs:read', 'sdk:download', 'audit:read', 'corridors:read',
  ],
  operator: [
    'tenant:read', 'tenant:verify_email', 'reports:read', 'reports:export', 'safety:read', 'support:read', 'support:write',
    'docs:read', 'sessions:read', 'sessions:write', 'vehicles:read', 'vehicles:write',
    'assignments:write', 'events:read', 'events:write', 'corridors:read',
  ],
  developer: [
    'tenant:read', 'tenant:verify_email', 'keys:read', 'keys:write', 'webhooks:read', 'webhooks:write',
    'docs:read', 'sdk:download', 'support:read', 'support:write', 'reports:read', 'safety:read', 'events:read',
  ],
  // Mirrors functions/src/cvsi/portalAuth.ts#CUSTOMER_SCOPES — kept in sync manually (see file header).
  analyst: ['tenant:read', 'tenant:verify_email', 'reports:read', 'reports:export', 'safety:read', 'events:read', 'usage:read', 'corridors:read'],
  billing: ['billing:read', 'usage:read', 'reports:read'],
  viewer: ['tenant:read', 'tenant:verify_email', 'reports:read', 'safety:read', 'events:read', 'docs:read', 'corridors:read'],
} as const;

/**
 * Route/map policy: every authenticated customer-portal role already has
 * reports:read, which is the governed scope for route evidence and maps.
 * This keeps precise-location viewing available to any CVSI portal member;
 * tenant isolation remains enforced by the backend tenant-scoped session.
 */

export type CustomerPortalRole = keyof typeof CUSTOMER_PORTAL_ROLES;
export type CustomerPortalScope = (typeof CUSTOMER_PORTAL_ROLES)[CustomerPortalRole][number];

export const PORTAL_ROLES = Object.keys(CUSTOMER_PORTAL_ROLES) as CustomerPortalRole[];

/** True when `role` grants `scope`. Unknown roles grant nothing. */
export function can(role: CustomerPortalRole, scope: string): boolean {
  const scopes = CUSTOMER_PORTAL_ROLES[role] as readonly string[] | undefined;
  return scopes ? scopes.includes(scope) : false;
}

/** All scopes for a role, as a plain string[] (handy for building a Set in app state). */
export function scopesForRole(role: CustomerPortalRole): string[] {
  return [...(CUSTOMER_PORTAL_ROLES[role] as readonly string[])];
}
